import { createGameAssets } from "../game-assets.js";
import { CatMotion } from "./cat-motion.js";
import { paintCatPortrait } from "./cat-face-art.js";

/** Lightweight portrait rig for the tool, progress strip, and result dialogs. */
export class CatPortraits {
  constructor({
    clock,
    asset = createGameAssets("/").catRig,
    reducedMotion = () =>
      matchMedia("(prefers-reduced-motion: reduce)").matches,
  }) {
    this.clock = clock;
    this.asset = asset;
    this.reducedMotion = reducedMotion;
    this.entries = new Map();
    this.ready = false;
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const { target } of entries) {
        const entry = this.entries.get(target);
        if (entry) this.resize(entry);
      }
    });
  }
  async mount() {
    this.image = new Image();
    this.image.src = this.asset;
    await this.image.decode();
    if (this.destroyed) return;
    this.ready = true;
    for (const entry of this.entries.values()) this.prepare(entry);
    this.unsubscribe = this.clock.subscribe(
      (time) => this.draw(time),
      () => this.active(),
    );
    this.clock.wake();
  }
  register(host, seed = 0, reveal = false) {
    if (this.entries.has(host)) return;
    const entry = { host, motion: new CatMotion(seed), seed };
    if (reveal) entry.motion.reveal(this.clock.time);
    this.entries.set(host, entry);
    if (this.ready) this.prepare(entry);
  }
  unregister(host) {
    this.resizeObserver.unobserve(host);
    this.entries.delete(host);
  }
  prepare(entry) {
    const canvas = document.createElement("canvas");
    canvas.className = "cat-portrait-canvas";
    canvas.setAttribute("aria-hidden", "true");
    entry.canvas = canvas;
    entry.context = canvas.getContext("2d", { alpha: true });
    entry.host.replaceChildren(canvas);
    this.resizeObserver.observe(entry.host);
    this.resize(entry);
    this.clock.wake();
  }
  resize(entry) {
    if (!entry.canvas) return;
    const size = Math.max(
      24,
      Math.round(
        (entry.host.clientWidth || 70) * Math.min(devicePixelRatio || 1, 2),
      ),
    );
    if (entry.canvas.width !== size) {
      entry.canvas.width = size;
      entry.canvas.height = size;
    }
    entry.size = size;
    this.paint(entry, this.clock.time);
  }
  visible(entry) {
    const dialog = entry.host.closest("dialog");
    return entry.host.isConnected && (!dialog || dialog.open);
  }
  active() {
    if (!this.ready) return false;
    for (const entry of this.entries.values())
      if (
        this.visible(entry) &&
        (!this.reducedMotion() || entry.motion.reacting(this.clock.time))
      )
        return true;
    return false;
  }
  draw(time) {
    for (const [host, entry] of this.entries) {
      if (!host.isConnected) {
        this.resizeObserver.unobserve(host);
        this.entries.delete(host);
        continue;
      }
      if (this.visible(entry)) this.paint(entry, time);
    }
  }
  paint(entry, time) {
    if (entry.context && this.ready)
      paintCatPortrait(
        entry.context,
        this.image,
        entry.motion.sample(time, this.reducedMotion()),
        entry.size,
      );
  }
  react(mood) {
    for (const entry of this.entries.values())
      entry.motion.react(mood, this.clock.time);
    this.clock.burst(1450);
    this.draw(this.clock.time);
  }
  wake() {
    this.clock.wake();
  }
  destroy() {
    this.destroyed = true;
    this.unsubscribe?.();
    this.resizeObserver.disconnect();
    this.entries.clear();
  }
}
